// Per-browser push-subscription data layer (arc 101 slice 09). Reads
// `GET /v1/push/subscriptions` (the user's opted-in devices) and deletes one by
// endpoint. Pairs with `lib/push.ts` — that owns the enable/disable state-machine
// for THIS browser; this manages the LIST of all the user's devices. Push
// subscriptions are per-USER (not per-account), so the query key omits the active
// account — the list is the same whichever tenant you're viewing.

import { createQuery } from '@tanstack/solid-query';
import { apiFetch } from './api';
import type { Page } from './paged';
import { browserPushRuntime, type PushRuntime } from './push';
import { SESSION_TOKEN } from './session';

/** One device row from `GET /v1/push/subscriptions` (snake_case wire shape). */
export type PushSubscriptionSummary = {
  readonly id: string;
  readonly endpoint: string;
  readonly user_agent: string | null;
  readonly created_at: string;
};

/**
 * What this browser can say about ITSELF. Three OUTCOMES, not a `string | null`
 * collapsing two very different reasons: `not_subscribed` means we asked the push
 * API and it has no subscription here; `unavailable` means we could not ask at all
 * (no Push API, or no service worker became active within
 * `SERVICE_WORKER_READY_MS`). Only the first is a statement about the user.
 */
export type CurrentDevice =
  | { readonly kind: 'subscribed'; readonly endpoint: string }
  | { readonly kind: 'not_subscribed' }
  | { readonly kind: 'unavailable' };

/** The device list plus what THIS browser is — badges its own row and guards its
 *  remove (the current device is toggled off via `PushToggle`, which also clears
 *  the local subscription; a bare server-delete would leave the toggle lying "on"). */
export type PushDevices = {
  readonly subscriptions: readonly PushSubscriptionSummary[];
  readonly currentDevice: CurrentDevice;
};

/**
 * Ask the browser which subscription is ours, and NEVER hang or fail doing it.
 *
 * `unavailable` is a rendered STATE, not an error: a browser with no service
 * worker cannot do push, which is a fact about the browser rather than a failure
 * of this page, and no retry the user can press changes it for this session. The
 * whole server list still renders — it is authoritative — we just cannot mark a
 * row as "this device". The bound comes from `readyServiceWorker`, because
 * `navigator.serviceWorker.ready` never settles when no worker registers, so the
 * `catch` here is reachable only with a deadline in front of it.
 */
export async function probeCurrentDevice(runtime: PushRuntime): Promise<CurrentDevice> {
  if (!runtime.supported()) return { kind: 'unavailable' };
  try {
    const endpoint = await runtime.currentEndpoint();
    return endpoint === null ? { kind: 'not_subscribed' } : { kind: 'subscribed', endpoint };
  } catch {
    return { kind: 'unavailable' };
  }
}

export const PUSH_SUBSCRIPTIONS_QUERY_KEY = 'push_subscriptions';

/**
 * The user's subscribed devices, plus which row is the current browser. Reads the
 * server list and (where push is usable) this browser's local endpoint in ONE
 * query, so a single invalidation after the toggle flips refreshes both. Disabled
 * (idle) without a session, like `useNotificationPrefs`.
 *
 * The wire is the canonical `{data, next_cursor}` envelope with `next_cursor`
 * always null: a person's browsers are served whole, so there is no page two and
 * no Load-more. `PushDevices` below is NOT that envelope — it is this layer's own
 * composite of the server list with a value only this browser can answer.
 */
export function usePushSubscriptions(
  token: string | null = SESSION_TOKEN,
  runtime: PushRuntime = browserPushRuntime(token),
) {
  return createQuery<PushDevices>(() => ({
    queryKey: [PUSH_SUBSCRIPTIONS_QUERY_KEY, token],
    enabled: token !== null,
    queryFn: async () => {
      const { data: subscriptions } = await apiFetch<Page<PushSubscriptionSummary>>(
        '/v1/push/subscriptions',
        token,
      );
      return { subscriptions, currentDevice: await probeCurrentDevice(runtime) };
    },
  }));
}

/** Forget one device server-side by endpoint (revoke a browser from the list).
 *  The same route `disablePush` uses; for a REMOTE device there's no local
 *  subscription to unsubscribe, so the server delete is the whole operation. */
export async function deletePushSubscription(
  endpoint: string,
  token: string | null = SESSION_TOKEN,
): Promise<void> {
  await apiFetch('/v1/push/subscriptions', token, {
    method: 'DELETE',
    body: JSON.stringify({ endpoint }),
  });
}

/** Best-effort browser/OS split of a stored user-agent. Proper-noun names (never
 *  translated); `null` legs let the caller show a localized fallback. Deliberately
 *  tiny — a full UA parser is overkill for labelling a handful of devices. */
export type ParsedUserAgent = { readonly browser: string | null; readonly os: string | null };

export function parseUserAgent(ua: string | null): ParsedUserAgent {
  if (!ua) return { browser: null, os: null };
  return { browser: detectBrowser(ua), os: detectOs(ua) };
}

function detectBrowser(ua: string): string | null {
  // Order matters: Edge/Opera UAs also carry "Chrome"/"Safari", and Chrome carries
  // "Safari" — so the most specific token has to win first.
  if (/\bEdg(?:e|A|iOS)?\//.test(ua)) return 'Edge';
  if (/\bOPR\/|\bOpera\//.test(ua)) return 'Opera';
  if (/\bFirefox\/|\bFxiOS\//.test(ua)) return 'Firefox';
  if (/\b(?:Chrome|Chromium|CriOS)\//.test(ua)) return 'Chrome';
  if (/\bSafari\//.test(ua)) return 'Safari';
  return null;
}

function detectOs(ua: string): string | null {
  // iOS UAs contain "like Mac OS X" and Android UAs contain "Linux" — check the
  // mobile OS before the desktop token it embeds.
  if (/\bWindows NT\b/.test(ua)) return 'Windows';
  if (/\b(?:iPhone|iPad|iPod)\b/.test(ua)) return 'iOS';
  if (/\bAndroid\b/.test(ua)) return 'Android';
  if (/\bMac OS X\b|\bMacintosh\b/.test(ua)) return 'macOS';
  if (/\bLinux\b/.test(ua)) return 'Linux';
  return null;
}

/** The push-service host of a subscription endpoint (e.g. `fcm.googleapis.com`) —
 *  a compact secondary label that disambiguates two same-browser devices. Falls
 *  back to the raw string if the endpoint won't parse as a URL. */
export function pushServiceHost(endpoint: string): string {
  try {
    return new URL(endpoint).host;
  } catch {
    return endpoint;
  }
}
